<template>
  <div class="promotion-deals-filter">
    <FiltersForm
      :has-filters="hasFilters"
      :is-skeleton-loading="isSkeletonLoading"
      :is-global-country-filter="false"
      @filter="onFilterClick"
      @clear="onClearFilters"
    >
      <platform-select
        v-model="selectedCountry"
        :is-multiple="false"
        :custom-countries="enabledPromoCountries"
        attach
      />

      <v-text-field
        clearable
        v-model="search"
        type="search"
        :label="$t('deal_id')"
        prepend-inner-icon="search"
      />

      <BaseSelect
        v-if="!soloPlacement"
        inner-icon="dns"
        label="Listings"
        :value="placementLabel"
        class="v-input"
      >
        <v-radio-group
          v-model="placementId"
          class="mt-0 pt-0"
          hide-details
        >
          <v-radio
            v-for="n in placementsTypes"
            :key="n.id"
            :label="n.name"
            :value="n.id"
            class="ma-2"
          ></v-radio>
        </v-radio-group>
      </BaseSelect>

      <MerchantFilter
        :country-code="selectedCountry"
        ref="customMerchantMenu"
        :set-merchants="setMerchants"
        :restored-merchants="restoredMerchants"
        label="Merchants"
        class="merchant-filter"
        hide-details
      />

      <v-autocomplete
        clearable
        v-model="group"
        :items="groups"
        prepend-inner-icon="category"
        item-title="name"
        item-value="id"
        :return-object="true"
        :label="$t('category')"
        :loading="isLoadingGroups"
        v-model:search="groupSearch"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import stringEscape from 'js-string-escape';
  import FiltersForm from './FiltersBase';
  import { isEqual } from 'lodash-es'
  import MerchantFilter from './MerchantFilter';
  import {isStr, debounce, isNum, isNull} from '@/utils';
  import BaseSelect from '../../BaseSelect'
  import filterUrlSync from '@/mixins/filterUrlSync';

  const groupSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    mixins: [filterUrlSync],
    components: {
      MerchantFilter,
      FiltersForm,
      BaseSelect
    },
    props: {
      isSkeletonLoading: {
        type: Boolean,
        required: false
      },
      isDataLoaded: {
        type: Boolean,
        required: false
      },
      placementsTypes: {
        type: Array,
        required: true
      },
    },
    emits: ['clearFilters', 'filter', 'countryChanged'],
    data() {
      return {
        merchants: [],
        defaultMerchants: [],
        // Merchants restored from the URL, once getMerchantsByIds resolves
        // them to full objects (with a name) — passed down to MerchantFilter,
        // which has no other channel to receive a pre-selection through.
        restoredMerchants: [],
        // Guards the one call that would otherwise stomp a URL-restored
        // `merchants` — see setMerchants() below.
        isMerchantsRestorePending: false,
        group: null,
        search: '',
        placementId: null,
        oldPlacementId: null,
        groupSearch: '',
        isLoadingGroups: false,
        isRefreshingGroups: false,
        selectedCountry: '',
        oldSelectedCountry: '',
        urlConfig: [
          { field: 'search', urlKey: 'id', type: 'string' },
          {
            // Looked up by id, not fetched via the default-search page: a
            // restored group can sit outside whatever getGroups() returns
            // for an empty search.
            field: 'group', urlKey: 'group', type: 'number', prop: 'id',
            fetchByIds: 'getGroupsByIds',
          },
          { field: 'placementId', urlKey: 'placement', type: 'number' },
          { field: 'selectedCountry', urlKey: 'country', type: 'string' },
          {
            // Looked up by id: a merchant restored from the URL may sit
            // outside whatever page MerchantFilter's own search last fetched.
            field: 'merchants', urlKey: 'merchants', type: 'number[]', prop: 'id',
            fetchByIds: 'getMerchantsByIds',
          },
        ],
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      hasFilters() {
        const {
          search,
          isPlacementIdChanged,
          group,
          merchants,
          defaultMerchants,
          selectedCountry
        } = this;

        return (
          search !== '' ||
          isPlacementIdChanged ||
          isNum(group?.id) ||
          !isEqual(merchants, defaultMerchants) ||
          selectedCountry !== this.oldSelectedCountry
        );
      },
      isPlacementIdChanged() {
        if (this.oldPlacementId === null) return false;
        return this.placementId !== this.oldPlacementId;
      },
      placementLabel() {
        if (this.placementsTypes.length === 0 || this.placementId === null) return '';
        return this.placementsTypes.find(item => item.id === this.placementId).name
      },
      businessMerchants() {
        return this.$store.getters['promotions/businessMerchants'];
      },
      groups() {
        return this.$store.getters['pepper/groups'];
      },
      soloPlacement() {
        return this.placementsTypes.length === 1;
      },
    },
    watch: {
      selectedCountry(val) {
        // clear merchants to trigger autofill with business merchants
        if (this.$refs.customMerchantMenu) {
          this.$refs.customMerchantMenu.clearMerchants();
        }

        // prevent initial double call
        if (!this.isSkeletonLoading) {
          this.oldSelectedCountry = val;
          this.$emit('countryChanged', val);
        }
      },
      userCountries(val) {
        // Don't stomp a country already restored from the URL.
        if (val.length > 0 && !this.selectedCountry) {
          this.selectedCountry = val[0].code;
        }
      },
      isDataLoaded(val) {
        if (!val) return;

        this.defaultMerchants = [...this.merchants];

        if (this.placementId === null) {
          this.placementId = this.placementsTypes[0].id;
          this.oldPlacementId = this.placementsTypes[0].id;
        }

        this.onFilter();
      },
      businessMerchants(nextItems) {
        if (nextItems && isEqual(this.merchants, this.defaultMerchants)) {
          this.merchants = nextItems;
        }
        this.defaultMerchants = nextItems ? [...nextItems] : [];
      },
      placementsTypes(nextPlacements) {
        let placementId;

        if (nextPlacements.length === 1) {
          placementId = this.placementsTypes[0].id;
        } else {
          // eslint-disable-next-line array-callback-return
          this.placementsTypes.some(i => {
            if (i.id === 1) {
              placementId = i.id;
              return true;
            }
          });
        }

        // Don't stomp a placement already restored from the URL — but still
        // track the computed default as the baseline, so isPlacementIdChanged
        // correctly reports the restored value as an active filter.
        if (this.placementId === null) {
          this.placementId = placementId;
        }
        this.oldPlacementId = placementId;
      },
      group() {
        this.isRefreshingGroups = true;
      },
      groupSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingGroups) {
            this.isRefreshingGroups = false;
            return false;
          }

          groupSearchDebouncer(() => this.getGroups(nextSearch));
        }
      },
    },
    created() {
      // Don't stomp a country already restored from the URL by this mixin's
      // own created() hook, which runs before this one.
      if (!this.selectedCountry && this.userCountries.length > 0) {
        this.selectedCountry = this.userCountries[0].code;
        this.oldSelectedCountry = this.userCountries[0].code;
      }

      // A merchants restore (also done by this mixin's created(), which runs
      // before this one) sets `this.merchants` to id-only stubs. See
      // setMerchants() for why this flag exists.
      this.isMerchantsRestorePending = this.merchants.length > 0;
    },
    methods: {
      getMerchantsByIds(ids) {
        return this.$store.dispatch('pepper/getMerchantsByIds', { ids })
          .then((items) => {
            this.restoredMerchants = items;
            return items;
          })
          .finally(() => {
            // Only now is the restore actually settled — see setMerchants() for
            // why this can't be cleared any earlier. A failed lookup settles it
            // too, or every later deselect-all would be ignored.
            this.isMerchantsRestorePending = false;
          });
      },
      getGroupsByIds(ids) {
        this.isLoadingGroups = true;
        return this.$store
          .dispatch('pepper/getGroupsByIds', { ids })
          .finally(() => {
            this.isLoadingGroups = false;
          });
      },
      getGroups(search = '') {
        const query = {
          search,
          countries: [this.selectedCountry]
        };

        this.isLoadingGroups = true;

        return this.$store
          .dispatch('pepper/getGroups', query)
          .then((res) => {
            this.isLoadingGroups = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingGroups = false;
            return err;
          });
      },
      setMerchants(items) {
        // MerchantFilter's own businessMerchants watcher can fire more than
        // once with an empty array before its own fetch settles — e.g. once
        // immediately on mount, then again once that fetch actually
        // resolves — each time nothing is selected yet. Any one of those
        // would wipe out a merchants restore (id stubs set synchronously,
        // before MerchantFilter even mounts) if it lands before
        // getMerchantsByIds resolves, so ignore all of them for as long as
        // that restore is still pending, not just the first.
        if (this.isMerchantsRestorePending && items.length === 0) return;

        this.merchants = items;
      },
      onFilterClick() {
        if (this.$refs.customMerchantMenu) {
          this.$refs.customMerchantMenu.showSaveText();
        }

        this.oldPlacementId = this.placementId;

        this.onFilter();
      },
      onFilter() {
        const { merchants, search, group, placementId, selectedCountry } = this;

        this.syncFiltersToUrl();

        this.$emit('filter', {
          merchant_ids: merchants.map((m) => m.id),
          group_id: group?.id || null,
          thread_id: isNull(search) ? '' : stringEscape(search),
          placement_id: placementId,
          country: selectedCountry
        });
      },
      onClearFilters() {
        this.group = null;
        this.search = '';
        this.merchants = this.businessMerchants.length ? this.businessMerchants : [];
        this.restoredMerchants = [];
        // eslint-disable-next-line array-callback-return
        this.placementsTypes.some(i => {
          if (i.id === 1) {
            this.placement_id = i.id;
            return true;
          }
        });

        if (this.$refs.customMerchantMenu) {
          this.$refs.customMerchantMenu.clearMerchants();
        }

        this.clearFiltersUrl();
        this.$emit('clearFilters');
        this.onFilter();
      },
    },
  };
</script>

<style scoped lang="scss">
.merchant-filter {
  width: 100%;
  max-width: 270px;
}
</style>
